/**
 * How big this device draws an emoji, as a factor of Apple's size.
 *
 * Every phone draws emojis with its own font, and at the same font-size they
 * are not the same size: Apple's are 1em wide, Google's (every Android phone)
 * 1.25em. An emoji sized to fill a small tile on an iPhone spills over the
 * tile's frame on Android. Multiply an emoji's font-size by this to get the
 * same size everywhere: 1 on Apple, about 0.82 with Google's emojis.
 *
 * Measured once with a canvas, which falls back to the same system emoji font
 * as the page.
 */

/** Apple's 😀 at 100px: 100px wide, a 103px bitmap box. */
const APPLE_BOX_PX = 103;

let cached: number | null = null;

export function emojiFitScale(): number {
  if (cached !== null) return cached;
  cached = 1;
  // jsdom has no canvas and logs an error for trying
  if (typeof document === "undefined" || /jsdom/i.test(navigator.userAgent)) return cached;
  try {
    const ctx = document.createElement("canvas").getContext("2d");
    if (!ctx) return cached;
    ctx.font = "100px sans-serif";
    const m = ctx.measureText("😀");
    const box = Math.max(m.width, (m.actualBoundingBoxAscent || 0) + (m.actualBoundingBoxDescent || 0));
    if (box > 0) cached = Math.min(1, Math.max(0.7, APPLE_BOX_PX / box));
  } catch {
    // keep 1
  }
  return cached;
}

/** An emoji font-size in px that looks `applePx` big on every device. */
export function fitEmojiPx(applePx: number): number {
  return Math.round(applePx * emojiFitScale());
}
